import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Building2,
  Clock,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PlatformLayout } from "./platform-layout";
import { SeedFailureNotificationHistory } from "@/components/admin/seed-failure-notification-history";

type SeedFailureAlertBreakdownRow = {
  workspaceId: string;
  workspaceName: string | null;
  seedKey: string;
  error: string;
  firstSeenAt: string;
  lastAttemptAt: string;
  ageHours: number;
};

export type SeedFailureAlertBreakdown = {
  thresholdHours: number;
  oldestAgeHours: number;
  affectedWorkspaces: number;
  failureCount: number;
  failedSeedKeys: string[];
  sampleErrors: string[];
  rows: SeedFailureAlertBreakdownRow[];
};

type RetryResponse = {
  workspaceId: string;
  workspaceName: string | null;
  inserted: number;
  backfilled: number;
  failures: { seedKey: string; error: string }[];
  breakdown: SeedFailureAlertBreakdown;
};

export const SEED_FAILURE_ALERT_BREAKDOWN_QUERY_KEY = [
  "/api/platform/seed-failures/alert-breakdown",
] as const;

export function formatAge(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return "just now";
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(hours * 60));
    return `${minutes}m`;
  }
  if (hours < 24) {
    const h = Math.round(hours * 10) / 10;
    return `${h}h`;
  }
  const days = Math.round((hours / 24) * 10) / 10;
  return `${days}d`;
}

export function SeedFailureAlertBreakdownPanel() {
  const { toast } = useToast();

  const { data, isLoading, isError } = useQuery<SeedFailureAlertBreakdown>({
    queryKey: SEED_FAILURE_ALERT_BREAKDOWN_QUERY_KEY,
  });

  const retryMutation = useMutation({
    mutationFn: async (workspaceId: string): Promise<RetryResponse> => {
      const res = await apiRequest(
        "POST",
        "/api/platform/seed-failures/alert-breakdown/retry",
        { workspaceId },
      );
      return (await res.json()) as RetryResponse;
    },
    onSuccess: (result) => {
      // Push the post-retry breakdown into cache so the row disappears
      // (or shrinks) on the very next render without a refetch.
      queryClient.setQueryData(
        SEED_FAILURE_ALERT_BREAKDOWN_QUERY_KEY,
        result.breakdown,
      );
      // The companion list view shares the underlying failure rows, so
      // bust its cache too in case ops bounces between the two pages.
      queryClient.invalidateQueries({
        queryKey: ["/api/platform/seed-failures"],
      });
      const wsLabel =
        result.workspaceName ||
        data?.rows.find((r) => r.workspaceId === result.workspaceId)
          ?.workspaceName ||
        result.workspaceId;
      if (result.failures.length > 0) {
        toast({
          title: `Retry still failing for ${wsLabel}`,
          description: `${result.failures.length} preset template(s) still erroring after retry.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: `Retry succeeded for ${wsLabel}`,
          description: `Inserted ${result.inserted}, backfilled ${result.backfilled}.`,
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Retry failed",
        description:
          error?.message ||
          "Could not retry seed automations for that workspace.",
        variant: "destructive",
      });
    },
  });

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center h-48"
        data-testid="loading-seed-failure-alert-breakdown"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div
        className="p-4 sm:p-6 text-sm text-destructive"
        data-testid="error-seed-failure-alert-breakdown"
      >
        Could not load the seed-failure alert breakdown. Try refreshing the page.
      </div>
    );
  }

  return (
    <div
      className="p-4 sm:p-6 space-y-4 sm:space-y-6"
      data-testid="panel-seed-failure-alert-breakdown"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Past-Threshold Failures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="text-2xl font-bold"
              data-testid="stat-alert-failure-count"
            >
              {data.failureCount}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Across {data.affectedWorkspaces} workspace
              {data.affectedWorkspaces === 1 ? "" : "s"} (≥ {data.thresholdHours}h
              old).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Clock className="h-4 w-4 text-rose-500" />
              Oldest Outstanding
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="text-2xl font-bold"
              data-testid="stat-alert-oldest-age"
            >
              {data.oldestAgeHours > 0 ? `${formatAge(data.oldestAgeHours)}` : "—"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Time since the longest-broken row was first seen.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              Failed Seed Keys
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.failedSeedKeys.length === 0 ? (
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-no-failed-seedkeys"
              >
                No seed keys are alerting right now.
              </p>
            ) : (
              <div
                className="flex flex-wrap gap-1"
                data-testid="list-failed-seedkeys"
              >
                {data.failedSeedKeys.map((key) => (
                  <Badge
                    key={key}
                    variant="outline"
                    className="font-mono text-xs"
                    data-testid={`badge-failed-seedkey-${key}`}
                  >
                    {key}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {data.sampleErrors.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sample Errors
            </CardTitle>
            <CardDescription>
              Up to three distinct error strings from the rows below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul
              className="space-y-2"
              data-testid="list-sample-errors"
            >
              {data.sampleErrors.map((err, idx) => (
                <li
                  key={idx}
                  className="text-xs text-muted-foreground bg-slate-100 dark:bg-slate-800 rounded p-2 whitespace-pre-wrap break-words"
                  data-testid={`text-sample-error-${idx}`}
                >
                  {err}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Long-Standing Seed Failures
          </CardTitle>
          <CardDescription>
            {rows.length === 0
              ? `No workspaces have a seed failure older than ${data.thresholdHours}h.`
              : `${rows.length} (workspace, seedKey) pair${rows.length === 1 ? "" : "s"} broken for at least ${data.thresholdHours}h. Sorted by age — oldest first.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p
              className="text-center text-muted-foreground py-8"
              data-testid="text-empty-state-alert-breakdown"
            >
              All workspaces are healthy past the alert threshold.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        Workspace
                      </span>
                    </TableHead>
                    <TableHead>Seed Key</TableHead>
                    <TableHead>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Broken Since
                      </span>
                    </TableHead>
                    <TableHead>Last Attempt</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const rowKey = `${row.workspaceId}:${row.seedKey}`;
                    const isRetrying =
                      retryMutation.isPending &&
                      retryMutation.variables === row.workspaceId;
                    return (
                      <TableRow
                        key={rowKey}
                        data-testid={`row-alert-breakdown-${rowKey}`}
                      >
                        <TableCell>
                          <div
                            className="font-medium"
                            data-testid={`text-alert-workspace-name-${rowKey}`}
                          >
                            {row.workspaceName || (
                              <span className="italic text-muted-foreground">
                                (unnamed)
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {row.workspaceId}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {row.seedKey}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div
                            className="text-sm"
                            data-testid={`text-alert-age-${rowKey}`}
                          >
                            {formatAge(row.ageHours)} ago
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(row.firstSeenAt).toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-muted-foreground">
                            {new Date(row.lastAttemptAt).toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <div
                            className="text-xs text-muted-foreground bg-slate-100 dark:bg-slate-800 rounded p-2 whitespace-pre-wrap break-words"
                            data-testid={`text-alert-error-${rowKey}`}
                            title={row.error}
                          >
                            {row.error.slice(0, 280)}
                            {row.error.length > 280 ? "…" : ""}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={retryMutation.isPending}
                            onClick={() =>
                              retryMutation.mutate(row.workspaceId)
                            }
                            data-testid={`button-alert-retry-${rowKey}`}
                          >
                            <RefreshCw
                              className={`h-3 w-3 mr-1 ${isRetrying ? "animate-spin" : ""}`}
                            />
                            Retry
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function AlertsContent() {
  return (
    <>
      <div className="p-3 sm:p-6 space-y-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Alerts</h1>
          <p className="text-muted-foreground">
            Platform-ops alert surface. The breakdown below lists any
            workspaces whose boot-time seeder failures have crossed the alert
            threshold, and the notification history confirms whether the
            paging pipeline actually fired. Retry a workspace to clear its
            row without waiting for the next scheduler tick.
          </p>
        </div>
        <SeedFailureAlertBreakdownPanel />
        <SeedFailureNotificationHistory enabled />
      </div>
    </>
  );
}

export default function PlatformSeedFailureAlerts() {
  return <PlatformLayout><AlertsContent /></PlatformLayout>;
}
